export type TicketPreset = 'member' | 'guest';

export type TicketDefaultValues = {
  name: string;
  type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
  price: number;
  quantity: number | null;
  enabled: boolean;
  allowedAttendeeTypes: string[];
  formSchema: [];
};

const STORAGE_PREFIX = 'milora:event-ticket-defaults:';

export const BUILTIN_TICKET_DEFAULTS: Record<TicketPreset, TicketDefaultValues> = {
  member: {
    name: 'Member Ticket',
    type: 'MEMBER_ONLY',
    price: 0,
    quantity: null,
    enabled: true,
    allowedAttendeeTypes: ['SELF', 'MEMBER'],
    formSchema: [],
  },
  guest: {
    name: 'Guest / Visitor Ticket',
    type: 'OPEN_FOR_ALL',
    price: 0,
    quantity: null,
    enabled: true,
    allowedAttendeeTypes: ['GUEST'],
    formSchema: [],
  },
};

function storageKey(communityId: string): string {
  return `${STORAGE_PREFIX}${communityId}`;
}

type StoredDefaults = Partial<Record<TicketPreset, TicketDefaultValues>>;

function readStored(communityId: string): StoredDefaults {
  if (!communityId || typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(storageKey(communityId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredDefaults;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(communityId: string, value: StoredDefaults) {
  if (!communityId || typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey(communityId), JSON.stringify(value));
}

export function getTicketDefault(
  communityId: string,
  preset: TicketPreset
): TicketDefaultValues {
  const stored = readStored(communityId)[preset];
  if (!stored) return { ...BUILTIN_TICKET_DEFAULTS[preset], formSchema: [] };

  return {
    name: stored.name?.trim() || BUILTIN_TICKET_DEFAULTS[preset].name,
    type: stored.type || BUILTIN_TICKET_DEFAULTS[preset].type,
    price: typeof stored.price === 'number' && stored.price >= 0 ? stored.price : 0,
    quantity:
      typeof stored.quantity === 'number' && stored.quantity >= 0 ? stored.quantity : null,
    enabled: stored.enabled !== false,
    allowedAttendeeTypes:
      Array.isArray(stored.allowedAttendeeTypes) && stored.allowedAttendeeTypes.length > 0
        ? stored.allowedAttendeeTypes
        : BUILTIN_TICKET_DEFAULTS[preset].allowedAttendeeTypes,
    formSchema: [],
  };
}

export function saveTicketDefault(
  communityId: string,
  preset: TicketPreset,
  ticket: {
    name: string;
    type: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
    price: number;
    quantity?: number | null;
    allowedAttendeeTypes: string[];
  }
) {
  const current = readStored(communityId);
  current[preset] = {
    name: ticket.name.trim() || BUILTIN_TICKET_DEFAULTS[preset].name,
    type: ticket.type,
    price: ticket.price >= 0 ? ticket.price : 0,
    quantity:
      typeof ticket.quantity === 'number' && ticket.quantity >= 0 ? ticket.quantity : null,
    enabled: true,
    allowedAttendeeTypes:
      ticket.allowedAttendeeTypes.length > 0
        ? ticket.allowedAttendeeTypes
        : BUILTIN_TICKET_DEFAULTS[preset].allowedAttendeeTypes,
    formSchema: [],
  };
  writeStored(communityId, current);
}

export function ticketFromPreset(communityId: string, preset: TicketPreset) {
  return {
    ...getTicketDefault(communityId, preset),
    preset,
  };
}
